import { ChangeDetectionStrategy, Component, ElementRef } from '@angular/core';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { AlertButton, AlertButtonRole, AlertOptions } from 'providers/alert-controller';
import { PopoverController } from 'providers/popover-controller';
import { LL } from 'i18n/i18n.service';

export interface AlertSelectOption {
  label: string;
  value: string;
}

export interface AlertInput {
  type?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  label?: string;
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  handler?: Function;
  min?: number;
  max?: number;
  selectOptions?: AlertSelectOption[];
}

@Component({
    selector: 'cs-alert',
    templateUrl: 'cs-alert.html',
    host: { 'class': 'alert' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertPopover extends CfPopoverBase {
  protected LL = LL;
  options: AlertOptions<any>;

  private buttonClicked: AlertButton<any> = undefined;

  constructor(private popoverController: PopoverController, private elementRef: ElementRef<HTMLElement>) {
    super();
  }

  afterAnimationFinished() {
    // auto-focus the first text input when the alert has one
    const input = this.elementRef.nativeElement.querySelector<HTMLInputElement>('.alert-input input');
    if (input) {
      input.focus();
    }
  }

  /**
   * NOTE: `data.resultData` may be a Promise if the button's `result` is an async function.
   * Callers that read `resultData` must await it if it could be a thenable.
   * `showAlertAsync` handles this via `Promise.race`.
   */
  onWillDismiss(data: any): void {
    if (this.buttonClicked) {
      let isFunction = (x: any): x is Function => typeof x === 'function';
      if (isFunction(this.buttonClicked.result)) {
        data.resultData = this.buttonClicked.result(this.buttonClicked.role === AlertButtonRole.Cancel ? null : this.getValues());
      } else {
        data.resultData = this.buttonClicked.result;
      }
    } else {
      // No button clicked (X button, backdrop, browser Back, or an external call to
      // popoverController.dismiss() — NOT the externalDismiss option, which never
      // reaches this line).
      // Use implicitCancelResult if provided, otherwise undefined.
      //
      // Deliberately does NOT fall back to the `role: 'cancel'` button's result. That
      // looks like the obvious improvement and it would break `exercise-switch.ts`,
      // which pairs a Cancel role button ("This session only") with `result: true`,
      // supplies no implicitCancelResult, and then treats `undefined` as "the user
      // dismissed, stay on this page". With the fallback, a stray backdrop tap would
      // return `true` there and switch the user's exercise for the session. A caller
      // that wants a value for the dismiss case sets implicitCancelResult.
      data.resultData = this.options.implicitCancelResult;
    }
  }

  handleButtonClick(event: Event, button: AlertButton<any>) {
    void event;
    this.buttonClicked = button;
    void this.popoverController.dismiss(this);
  }

  handleCloseClick() {
    // Dismiss without setting a button - same as backdrop dismiss
    void this.popoverController.dismiss(this);
  }

  /**
   * A stable name an E2E test can select this button by, so tests never have to
   * match the button's rendered English words.
   *
   * The name comes from the value the button hands back to its caller — the
   * "throw the work away" button of every leave-this-page dialog answers
   * `'discard'`, so every one of them renders `e2e-locator="alert-discard"`
   * whatever its label says. That matters because the labels differ and change:
   * the shared unsaved-changes guard writes "Discard", the meal planner writes
   * "Exit & don't save my changes.", and both are translated into three more
   * languages. A test that matched the text would break on any reword.
   *
   * Returns null — and so renders no attribute — when the result is not a plain
   * value we can put in an attribute and select on. `result` may also be a
   * function, or a free-form string such as a document id, and neither makes a
   * name worth selecting by.
   */
  protected e2eLocatorFor(button: AlertButton<any>): string | null {
    const result = button.result;
    // if the result is not a short, simple word, there is no stable name to give
    if (typeof result !== 'string' || !/^[a-z0-9][a-z0-9-]{0,39}$/i.test(result)) {
      return null;
    }
    return `alert-${result}`;
  }

  // Check if any button has long text (3+ words)
  hasLongButtons(): boolean {
    if (!this.options.buttons) {
      return false;
    }
    return this.options.buttons.some(button => {
      const textStr = button.text ? button.text.toString() : '';
      // Count spaces to determine if text has 3+ words
      const spaceCount = (textStr.match(/ /g) || []).length;
      return spaceCount >= 2; // 2+ spaces means 3+ words
    });
  }

  isRadioGroup(): boolean {
    return this.options.inputs?.length > 0 && this.options.inputs.every(i => i.type === 'radio');
  }

  selectRadio(input: any) {
    this.options.inputs.forEach(i => i.checked = false);
    input.checked = true;
  }

  getValues(): any {
    if (this.options.inputs === undefined) {
      return undefined;
    }

    const inputTypes: string[] = [];
    this.options.inputs.forEach(input => {
      if (inputTypes.indexOf(input.type) < 0) {
        inputTypes.push(input.type);
      }
    });

    if (inputTypes.length > 1 && (inputTypes.indexOf('checkbox') > -1 || inputTypes.indexOf('radio') > -1)) {
      console.warn(`Alert cannot mix input types: ${(inputTypes.join('/'))}. Please see alert docs for more info.`);
    }

    let inputType = inputTypes.length ? inputTypes[0] : null;

    if (inputType === 'radio') {
      // this is an alert with radio buttons (single value select)
      // return the one value which is checked, otherwise undefined
      const checkedInput = this.options.inputs.find(i => i.checked);
      return checkedInput ? checkedInput.value : undefined;
    }

    if (inputType === 'checkbox') {
      // this is an alert with checkboxes (multiple value select)
      // return an array of all the checked values
      return this.options.inputs.filter(i => i.checked).map(i => i.value);
    }

    if (this.options.inputs.length === 0) {
      // this is an alert without any options/inputs at all
      return undefined;
    }

    // this is an alert with text inputs
    // return an object of all the values with the input name as the key
    const values: { [k: string]: string } = {};
    this.options.inputs.forEach(i => {
      values[i.name] = i.value;
    });
    return values;
  }

}
